import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  Alert,
  Chip,
  IconButton,
  Divider,
  Skeleton,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  useMediaQuery,
  useTheme,
  Tooltip
} from '@mui/material';
import {
  Announcement as AnnouncementIcon,
  Event as EventIcon,
  Photo as PhotoIcon,
  Forum as ForumIcon,
  HealthAndSafety as HealthIcon,
  Recommend as RecommendIcon,
  Restaurant as RecipeIcon,
  Flag as GoalIcon,
  Warning as EmergencyIcon,
  Celebration as FunIcon,
  Message as PostIcon,
  Description as DocIcon,
  Person as PersonIcon,
  ArrowForward as ArrowIcon,
  People as PeopleIcon,
  EventNote as EventsIcon,
  PhotoLibrary as PhotosIcon,
  Add as AddIcon,
  Cake as CakeIcon,
  Favorite as HeartIcon,
  Celebration as CelebrationIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import PendingInvites from '../../Components/PendingInvites';
import { getActivityFeed, getActivityIcon, getActivityColor, getActivityRoute } from '../../services/activityFeedService';
import { getFamilyStats, getUpcomingEvents, getUpcomingBirthdays } from '../../services/dashboardService';
import { formatDistanceToNow, format } from 'date-fns';
import QuickActionsConfig, { getSavedQuickActions, getQuickActionsByIds } from '../../Components/shared/QuickActionsConfig';
import './Home.css';
import logger from '../../utils/logger';

/**
 * Activity Icon Component
 * Displays the appropriate icon for each activity type
 */
const ActivityIcon = ({ type }) => {
  const iconName = getActivityIcon(type);
  const color = getActivityColor(type);

  const iconMap = {
    Announcement: AnnouncementIcon,
    Event: EventIcon,
    Photo: PhotoIcon,
    Forum: ForumIcon,
    HealthAndSafety: HealthIcon,
    Recommend: RecommendIcon,
    Restaurant: RecipeIcon,
    Flag: GoalIcon,
    Warning: EmergencyIcon,
    Celebration: FunIcon,
    Message: PostIcon,
    Description: DocIcon
  };

  const IconComponent = iconMap[iconName] || PostIcon;

  return (
    <Avatar sx={{ bgcolor: color, width: 40, height: 40 }}>
      <IconComponent fontSize="small" />
    </Avatar>
  );
};

/**
 * Home Page - Activity Dashboard
 * Central landing page showing recent family activity and quick stats
 */
const Home = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({
    totalMembers: 0,
    totalAnnouncements: 0,
    totalEvents: 0,
    totalPhotos: 0,
    totalPosts: 0
  });
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quickActions, setQuickActions] = useState(() => getQuickActionsByIds(getSavedQuickActions()));
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  // Handle config dialog close
  const handleConfigClose = (saved) => {
    setConfigDialogOpen(false);
    if (saved) {
      // Refresh quick actions from localStorage
      setQuickActions(getQuickActionsByIds(getSavedQuickActions()));
    }
  };

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.familyCode]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!currentUser?.familyCode) {
        // FamilyPortalGuard handles the redirect to /signup.
        // Just stop loading so the page doesn't stay on the skeleton.
        setLoading(false);
        return;
      }

      // Fetch activity feed, stats, events, and birthdays in parallel
      const [activityFeed, familyStats, events, upcomingBirthdays] = await Promise.all([
        getActivityFeed(currentUser.familyCode, 5),
        getFamilyStats(currentUser.familyCode),
        getUpcomingEvents(currentUser.familyCode, 3),
        getUpcomingBirthdays(currentUser.familyCode, 30),
      ]);

      setActivities(activityFeed);
      setStats(familyStats);
      setUpcomingEvents(events);
      setBirthdays(upcomingBirthdays);
    } catch (err) {
      logger.error('Error loading dashboard:', err);
      setError('Failed to load dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for birthdays
  const getDaysLabel = (daysUntil) => {
    if (daysUntil === 0) return 'Today!';
    if (daysUntil === 1) return 'Tomorrow';
    return `In ${daysUntil} days`;
  };

  const getChipColor = (daysUntil) => {
    if (daysUntil === 0) return 'error';
    if (daysUntil <= 7) return 'warning';
    return 'default';
  };

  // Render loading skeleton
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 3, borderRadius: 2 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Welcome Header */}
      <Paper
        elevation={3}
        sx={{
          mb: 4,
          p: { xs: 2, sm: 4 },
          background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 50%, #5d4037 100%)',
          color: 'white',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(46, 125, 50, 0.3)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
          <Avatar
            src={currentUser?.photoURL}
            alt={currentUser?.displayName || 'User'}
            sx={{ width: { xs: 52, sm: 72 }, height: { xs: 52, sm: 72 }, mr: { xs: 1.5, sm: 3 }, border: '3px solid white' }}
          >
            <PersonIcon sx={{ fontSize: { xs: 28, sm: 40 } }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 0.5,
                color: 'white',
                fontSize: { xs: '1.35rem', sm: '3rem' },
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                wordBreak: 'break-word'
              }}
            >
              Welcome back, {currentUser?.displayName || userData?.firstName || 'User'}!
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
              {userData?.familyCode && `Family Code: ${userData.familyCode}`}
            </Typography>
          </Box>
        </Box>

        {/* Quick Stats - Clickable */}
        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mt: { xs: 1, sm: 2 } }}>
          {[
            { to: '/contacts', icon: PeopleIcon, value: stats.totalMembers, label: 'Members' },
            { to: '/photos?tab=family', icon: PhotosIcon, value: stats.totalPhotos, label: 'Family Photos' },
            { to: '/events', icon: EventsIcon, value: stats.totalEvents, label: 'Events' },
            { to: '/announcements', icon: AnnouncementIcon, value: stats.totalAnnouncements, label: 'Announcements' },
          ].map(({ to, icon: Icon, value, label }) => (
            <Grid item xs={6} sm={3} key={label}>
              <Box
                component={Link}
                to={to}
                sx={{
                  textAlign: 'center',
                  p: { xs: 1, sm: 2 },
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: 2,
                  display: 'block',
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.35)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Icon sx={{ fontSize: { xs: 24, sm: 32 }, mb: 0.5, color: 'white' }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  {value}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  {label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Pending Invitations — shown prominently if user has any */}
      <PendingInvites />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Recent Activity Feed */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                Recent Activity
              </Typography>
              <IconButton
                component={Link}
                to="/family-board"
                color="primary"
                sx={{ '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
              >
                <ArrowIcon />
              </IconButton>
            </Box>

            {activities.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <PostIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No recent activity
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Be the first to share something with your family!
                </Typography>
              </Box>
            ) : (
              <Box>
                {activities.map((activity, index) => (
                  <Box key={`${activity.type}-${activity.id}-${index}`}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        py: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover', borderRadius: 1, px: 1, mx: -1 },
                        transition: 'all 0.2s'
                      }}
                      onClick={() => navigate(getActivityRoute(activity.type, activity.id))}
                    >
                      <Box sx={{ mr: 2 }}>
                        <ActivityIcon type={activity.type} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <Chip
                            label={activity.type.replace('_', ' ')}
                            size="small"
                            sx={{
                              bgcolor: getActivityColor(activity.type),
                              color: 'white',
                              fontWeight: 600,
                              mr: 1,
                              textTransform: 'capitalize'
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {activity.timestamp && formatDistanceToNow(activity.timestamp, { addSuffix: true })}
                          </Typography>
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {activity.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {activity.description}
                        </Typography>
                        {activity.author && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            by {activity.author}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {index < activities.length - 1 && <Divider />}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right Column - Quick Actions, Events & Birthdays */}
        <Grid item xs={12} md={4}>
          {/* Quick Actions */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AddIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Quick Actions
                </Typography>
              </Box>
              <Tooltip title="Customize Quick Actions">
                <IconButton
                  size="small"
                  onClick={() => setConfigDialogOpen(true)}
                  sx={{ color: 'text.secondary' }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}
            >
              {quickActions.map((action) => {
                const IconComponent = action.icon;
                return (
                  <Button
                    key={action.id}
                    variant="outlined"
                    size="small"
                    startIcon={<IconComponent />}
                    onClick={() => navigate(action.path)}
                    sx={{
                      borderColor: action.color,
                      color: action.color,
                      minWidth: isMobile ? 'calc(50% - 4px)' : 'auto',
                      '&:hover': {
                        borderColor: action.color,
                        bgcolor: `${action.color}15`
                      }
                    }}
                  >
                    {action.label}
                  </Button>
                );
              })}
            </Box>
          </Paper>

          {/* Upcoming Events */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EventIcon sx={{ mr: 1, color: 'secondary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Upcoming Events
                </Typography>
              </Box>
              <IconButton component={Link} to="/events" size="small">
                <ArrowIcon />
              </IconButton>
            </Box>
            {upcomingEvents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No upcoming events
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/events?action=create')}
                  sx={{ mt: 1 }}
                >
                  Create one
                </Button>
              </Box>
            ) : (
              <List dense sx={{ p: 0 }}>
                {upcomingEvents.slice(0, 3).map((event) => (
                  <ListItem
                    key={event.id}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: 'background.default',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => navigate('/events')}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'secondary.main' }}>
                        <EventIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={event.title}
                      secondary={event.date ? format(event.date.toDate ? event.date.toDate() : new Date(event.date), 'MMM d, yyyy') : 'Date TBD'}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          {/* Birthdays & Anniversaries */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CakeIcon sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Birthdays & Anniversaries
                </Typography>
              </Box>
              <IconButton component={Link} to="/birthdays" size="small" title="View birthday calendar">
                <ArrowIcon />
              </IconButton>
            </Box>
            {birthdays.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CakeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No upcoming celebrations in the next 30 days
                </Typography>
              </Box>
            ) : (
              <List dense sx={{ p: 0 }}>
                {birthdays.slice(0, 4).map((item) => (
                  <ListItem
                    key={item.id}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: item.isToday ? 'action.selected' : 'background.default',
                      border: item.isToday ? 2 : 0,
                      borderColor: item.isToday ? 'warning.main' : 'transparent'
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={item.photoUrl}
                        sx={{
                          bgcolor: item.type === 'birthday' ? 'warning.main' : 'error.main'
                        }}
                      >
                        {item.type === 'birthday' ? <CakeIcon /> : <HeartIcon />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {item.name}
                          </Typography>
                          {item.isToday && (
                            <CelebrationIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {item.type === 'birthday'
                              ? `Turning ${item.age}`
                              : `${item.years} years`
                            }
                          </Typography>
                          <Chip
                            label={getDaysLabel(item.daysUntil)}
                            size="small"
                            color={getChipColor(item.daysUntil)}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions Configuration Dialog */}
      <QuickActionsConfig
        open={configDialogOpen}
        onClose={handleConfigClose}
      />
    </Container>
  );
};

export default Home;
